import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Permission, Scope } from '../../api/types/permissions';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Banner } from '../ui/Banner';
import { Loading } from '@fiado/react-kit';
import { groupByAction } from '../../lib/permissionOrder';
import {
    ownCatalogQueryOptions,
    useLabelResolver,
    type LabelResolver,
} from '../../hooks/useLabelResolver';
import {
    tenantCatalogQueryOptions,
    useTenantLabelResolver,
} from '../../hooks/useTenantLabelResolver';

const HIDDEN_TITLE = 'Otros permisos del rol';
const HIDDEN_HINT =
    'Este rol tiene permisos que no puedes asignar desde aquí. Se conservan al guardar salvo que los desmarques.';

/** Forma a la que se proyecta el catálogo para pintarlo. */
interface PickerItem {
    key: Permission;
    category: string;
    displayName: string;
    description: string;
}

interface Props {
    value: Permission[];
    onChange: (next: Permission[]) => void;
    /** Sin valor excluye los PLATFORM; `'PLATFORM'` ofrece solo esos. */
    scopeFilter?: Scope;
    /** Tenant visto desde plataforma: su catálogo (`?tenantId`) y sus etiquetas reemplazan a las del actor. */
    tenantId?: string;
}

export function PermissionPicker(props: Props) {
    return props.tenantId ? (
        <TenantPermissionPicker {...props} tenantId={props.tenantId} />
    ) : (
        <OwnPermissionPicker {...props} />
    );
}

function TenantPermissionPicker(props: Props & { tenantId: string }) {
    const labels = useTenantLabelResolver(props.tenantId);
    return <PermissionPickerBody {...props} labels={labels} />;
}

function OwnPermissionPicker(props: Props) {
    const labels = useLabelResolver();
    return <PermissionPickerBody {...props} labels={labels} />;
}

function PermissionPickerBody({
    value,
    onChange,
    scopeFilter,
    tenantId,
    labels,
}: Props & { labels: LabelResolver }) {
    // Comparte caché con el catálogo del resolvedor de etiquetas.
    const catalogQuery = useQuery(
        tenantId ? tenantCatalogQueryOptions(tenantId) : ownCatalogQueryOptions(),
    );

    if (catalogQuery.isLoading) return <Loading />;

    // Sin catálogo no se pinta el picker vacío: con él vacío, todo el rol pasaría a «fuera del catálogo».
    if (catalogQuery.isError) {
        return (
            <Banner variant="error" title="No se pudo cargar el catálogo de permisos">
                Inténtalo de nuevo.
            </Banner>
        );
    }

    const items: PickerItem[] = (catalogQuery.data?.items ?? [])
        .filter((p) => (scopeFilter ? p.scope === scopeFilter : p.scope !== 'PLATFORM'))
        .filter((p) => !p.isSystemOnly)
        .map((p) => ({
            key: p.key as Permission,
            category: p.category,
            displayName: labels.permission(p.key).name,
            description: p.description,
        }));

    // Permisos que el rol ya tiene y el picker no ofrece: se muestran aparte para no perderlos al guardar.
    const catalogKeys = new Set(items.map((p) => p.key));
    const heldKeys = value.filter((k) => !catalogKeys.has(k));

    const grouped = items.reduce<Record<string, PickerItem[]>>((acc, p) => {
        (acc[p.category] ??= []).push(p);
        return acc;
    }, {});

    const toggle = (key: Permission): void => {
        onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
    };

    return (
        <div className="space-y-4">
            {Object.entries(grouped)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, perms]) => (
                    <section key={category} className="space-y-3">
                        <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                            {category}
                        </h3>
                        {groupByAction(perms).map(({ group, items: groupItems }) => (
                            <div key={group.id} className="space-y-2 pl-3 border-l">
                                <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">
                                    {group.label}
                                </h4>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {groupItems.map((p) => (
                                        <Label
                                            key={p.key}
                                            className="flex items-start gap-2 cursor-pointer"
                                        >
                                            <Checkbox
                                                checked={value.includes(p.key)}
                                                onCheckedChange={() => {
                                                    toggle(p.key);
                                                }}
                                                className="mt-0.5"
                                            />
                                            <span className="flex flex-col">
                                                <span className="text-sm font-medium leading-tight">
                                                    {p.displayName}
                                                </span>
                                                <span className="text-xs text-muted-foreground leading-tight">
                                                    {p.description}
                                                </span>
                                            </span>
                                        </Label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                ))}

            <HeldKeysSection
                keys={heldKeys}
                labels={labels}
                title={HIDDEN_TITLE}
                hint={HIDDEN_HINT}
                onToggle={toggle}
            />
        </div>
    );
}

interface HeldKeysSectionProps {
    keys: Permission[];
    labels: LabelResolver;
    title: string;
    hint: string;
    onToggle: (key: Permission) => void;
}

/** Sección aparte con los permisos del rol que el picker no ofrece, marcados y con su nombre. */
function HeldKeysSection({ keys, labels, title, hint, onToggle }: HeldKeysSectionProps) {
    const titleId = useId();
    if (keys.length === 0) return null;
    const entries = keys
        .map((key) => ({ key, ...labels.permission(key) }))
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <section aria-labelledby={titleId} className="space-y-3">
            <h3 id={titleId} className="text-sm font-semibold uppercase text-muted-foreground">
                {title}
            </h3>
            <p className="text-xs text-muted-foreground">{hint}</p>
            <div className="grid sm:grid-cols-2 gap-2">
                {entries.map(({ key, name, description }) => (
                    <Label key={key} className="flex items-start gap-2 cursor-pointer">
                        <Checkbox
                            checked
                            onCheckedChange={() => {
                                onToggle(key);
                            }}
                            className="mt-0.5"
                        />
                        <span className="flex flex-col">
                            <span className="text-sm font-medium leading-tight">{name}</span>
                            {description && (
                                <span className="text-xs text-muted-foreground leading-tight">
                                    {description}
                                </span>
                            )}
                        </span>
                    </Label>
                ))}
            </div>
        </section>
    );
}
